/* eslint-disable @typescript-eslint/no-non-null-assertion */

/** Position of selection range */
export interface SelectionRangePosition {
  /** The start position of selectable range. */
  selectionStart: number;
  /** The end position of selectable range. */
  selectionEnd: number;
  /** The next possible selection position if any. */
  selectionNextIndex: number;
  /** The previous possible selection position if any. */
  selectionPreviousIndex: number;
}

/** Selectable context for each character index of string. */
export interface SelectionRange extends SelectionRangePosition {
  /** Whether the character is a unit representing a date, i.e. Y, m, D, etc. */
  isDateUnit: boolean;
  /** The phrase which the character belongs. */
  phrase: string;
  /** The character (date units are masked with *). */
  char: string;
}

/**
 * The `DatesElementAdapter` contains methods and properties for intercepting user interaction with date input elements.
 */
export class DatesElementAdapter {
  /** Masked value of input. */
  get value() {
    return this._value;
  }
  set value(value: string | null) {
    this._value = value ?? this.getMaskedValue();
  }
  private _value: string | null = "";

  // List of selection ranges for provided input element
  private _selectionRanges: Array<SelectionRange> = [];
  // Selection range currently selected
  private _selectionPosition: SelectionRangePosition = this._selectionRanges[0];

  /** Moves selection to specific character index. */
  setSelection(charPosition?: number | null) {
    if (charPosition != null) {
      if (charPosition >= this._selectionRanges.length)
        this._selectionPosition = this._selectionRanges[this._selectionRanges.length - 1];
      else if (charPosition < 0) this._selectionPosition = this._selectionRanges[0];
      else this._selectionPosition = this._selectionRanges[charPosition];
    }
    this.setSelectionFromSelectionRange();
  }
  /** Moves selection to the next selectable range if possible. */
  setSelectionNext(callback?: () => void) {
    if (this._selectionPosition.selectionNextIndex >= 0) {
      this._selectionPosition = this._selectionRanges[this._selectionPosition.selectionNextIndex];
      this.setSelectionFromSelectionRange();
      if (callback) callback();
    }
  }
  /** Moves selection to the previous selectable range if possible. */
  setSelectionPrevious(callback?: () => void) {
    if (this._selectionPosition.selectionPreviousIndex >= 0) {
      this._selectionPosition = this._selectionRanges[this._selectionPosition.selectionPreviousIndex];
      this.setSelectionFromSelectionRange();
      if (callback) callback();
    }
  }
  /** Adds character and mask input for selected range. */
  setInputMask(character: string) {
    const selection = document.getSelection()!.toString();
    const selectionLength = selection.toString().length;
    const sectionValue = /^[0-9]*$/.test(selection)
      ? // If the user has completed a full date, and entered an additional character
        // like 5 create a new mask, for example 1234 => ***5
        `${character}`.padStart(selectionLength, "*")
      : // The user has not yet completed a full date, keep masking until the section has completed
        `${selection.replace(/\*/g, "")}${character}`.padStart(selectionLength, "*");
    const startValue = this.value!.substring(0, this.inputElement.selectionStart!);
    const endValue = this.value!.substring(this.inputElement.selectionStart! + selectionLength);
    this.value = `${startValue}${sectionValue}${endValue}`;
  }
  /** Clears input mask from numbers for deleted range. */
  clearInputMask() {
    const selection = document.getSelection()!.toString();
    const selectionLength = selection.toString().length;
    const sectionValue = selection.replace(/./g, "*");
    const startValue = this.value!.substring(0, this.inputElement.selectionStart!);
    const endValue = this.value!.substring(this.inputElement.selectionStart! + selectionLength);
    this.value = `${startValue}${sectionValue}${endValue}`;
  }

  constructor(protected inputElement: HTMLInputElement, protected dateFormat: string) {
    this._selectionRanges = (dateFormat.match(/'[^']*'|(.)\1*/g) ?? [])
      .map(this.toSelectionRange)
      .reduce(this.toChars, [])
      .map(this.toSelectionNavigation);
    this._selectionPosition = this._selectionRanges[0];
    this._value = this.getMaskedValue();
  }

  /** Moves selection to current selection range. */
  private setSelectionFromSelectionRange() {
    this.inputElement.setSelectionRange(this._selectionPosition.selectionStart, this._selectionPosition.selectionEnd);
  }

  /** Returns a masked string based on current value in the input. */
  private getMaskedValue() {
    return this._selectionRanges
      .map((selection, index) => {
        const char = this.inputElement.value.charAt(index);
        return selection.isDateUnit && /[0-9]/.test(char) ? char : selection.char;
      })
      .join("");
  }
  /** Returns whether the provided input represent a date unit like Y, m, D, etc. */
  private isDateUnit(value: string) {
    return /^[Yy|Mm|Dd|Ww]*$/.test(value);
  }
  /** Generates selection start and end properties for selection range object. */
  private toSelectionRange = (encodedPhrase: string, phraseIndex: number, encodedPhrases: Array<string>) => {
    const phrases = encodedPhrases.map((encodedPhrase) => encodedPhrase.replace(/'/g, ""));
    const phrase = phrases[phraseIndex];

    if (!this.isDateUnit(encodedPhrase))
      return {
        char: "",
        phrase,
        isDateUnit: false,
        selectionStart: -1,
        selectionEnd: -1,
        selectionNextIndex: -1,
        selectionPreviousIndex: -1,
      };

    const precedingPhraseIndex = phrases.findIndex((searchPhrase) => this.isDateUnit(searchPhrase) && searchPhrase === phrase);
    const selectionStart = phrases.slice(0, precedingPhraseIndex).reduce((sum, phrase) => sum + phrase.length, 1) - 1;
    return {
      char: "",
      phrase,
      isDateUnit: true,
      selectionStart,
      selectionEnd: selectionStart + phrase.length,
      selectionNextIndex: -1,
      selectionPreviousIndex: -1,
    };
  };
  /** Generate next and previous selection property for selection range object. */
  private toSelectionNavigation = (
    selectionRange: SelectionRange,
    selectionRangeIndex: number,
    selectionRanges: Array<SelectionRange>
  ) => {
    const selectionNextIndex = selectionRanges.findIndex(
      (searchSelectionRange, searchSelectionRangeIndex) =>
        searchSelectionRange.isDateUnit &&
        searchSelectionRangeIndex > selectionRangeIndex &&
        searchSelectionRange.phrase !== selectionRange.phrase
    );
    const selectionPreviousIndex = selectionRanges.findIndex(
      (searchSelectionRange, searchSelectionRangeIndex) =>
        searchSelectionRange.isDateUnit &&
        searchSelectionRangeIndex < selectionRangeIndex &&
        searchSelectionRange.phrase !== selectionRange.phrase
    );
    return { ...selectionRange, selectionNextIndex, selectionPreviousIndex };
  };
  /** Generates char property for selection range object. */
  private toChars = (selectionRanges: Array<SelectionRange>, selectionRange: SelectionRange) => {
    selectionRange.phrase.split("").forEach((unmaskedChar) => {
      const char = selectionRange.isDateUnit ? "*" : unmaskedChar;
      selectionRanges.push({ ...selectionRange, char });
    });
    return selectionRanges;
  };
}
